import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useStreetViewPanoramaLocation } from '~/_shared/components/streetViewPanorama/useStreetViewPanoramaLocation';
import { type LatLng } from '~/_shared/types/latLng';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { useMap } from '~/map/map/mapContext';
import { frontendStateStreetViewSet } from '~/store/frontendState/mapTools/streetView/streetView.actionCreators';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { type ProximityType } from '../../_shared/types/proximity/proximity.enums';
import { type GeoLocation } from '../../_shared/utils/geolocation/geolocation';
import { useLocationFromGeolocation } from '../../_shared/utils/hooks/useLocationFromGeolocation';
import { usePrevious } from '../../_shared/utils/hooks/usePrevious';
import { useModal } from '../../modal/useModal.hook';
import { frontendStateDirectionsAddMarkerWaypoint } from '../../store/frontendState/mapTools/directions/directions.actionCreators';
import { type DirectionSourceType } from '../../store/frontendState/mapTools/directions/directions.state';
import {
  selectDistanceCalculatorDrawingMode,
  startDistanceCalculator,
} from '../../store/frontendState/mapTools/distanceCalculator/distanceCalculator.actionCreators';
import { type DistanceCalculatorDrawingModeEnum } from '../../store/frontendState/mapTools/distanceCalculator/distanceCalculator.state';
import {
  setStartingLocation, setType,
} from '../../store/frontendState/proximityTool/proximityTool.actionCreators';
import { useMapSettingsDirectionsShouldBeginAtUserLocationSelector } from '../../store/mapSettings/directions/mapSettingsDirections.selectors';
import { usePublicMapSettingsSelector } from '../../store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '../../store/selectors/useMapInfoSelectors';
import { MapContextMenuComponent } from './mapContextMenu.component';

export const MapContextMenuContainer: FC = () => {
  const dispatch = useDispatch();
  const directionsUseUserLocation = useMapSettingsDirectionsShouldBeginAtUserLocationSelector();
  const isPresentationalView = useIsMapPresentationalSelector();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const publicMapSettings = usePublicMapSettingsSelector();
  const { openModal: openFindLocation } = useModal('LocationFinder');
  const [isLoading, setIsLoading] = useState(false);
  const [latLng, setLatLng] = useState<LatLng | null>(null);
  const previousLatLng = usePrevious(latLng);
  const [address, setAddress] = useState('');
  const { addLocationFromLatLng, getGeolocationFromLatLng } = useLocationFromGeolocation();
  const isMapInteractionActive = useIsMapInteractionActive();
  const mapInfo = useMapInfoDataSelector();
  const map = useMap();

  const { streetViewLocation } = useStreetViewPanoramaLocation(latLng);

  const isStreetViewAvailable = !!streetViewLocation
    && (!isPresentationalView || publicMapSettings.allowStreetView);

  const isAddMarkerAllowed = !isEditModeDisabled && !isLayeredMapConnected && !checkIsSalesforceMap(mapInfo);

  const handleAddressRequest = useCallback((
    params: { callback: (response: GeoLocation) => void}
  ) => {
    if (!latLng) {
      return;
    }

    getGeolocationFromLatLng({
      latLng,
      callback: params.callback,
      onIsLoadingChanged: setIsLoading,
    });
  }, [getGeolocationFromLatLng, latLng]);

  const handleGetAddress = useCallback(() =>
    handleAddressRequest({
      callback: response => {
        setAddress(response.address);
      },
    }), [handleAddressRequest]);

  const handleAddWaypoint = useCallback((directionSourceType: DirectionSourceType) =>
    handleAddressRequest({
      callback: (response) => {
        dispatch(frontendStateDirectionsAddMarkerWaypoint({
          address: response.address,
          latLng: response.latLng,
          startsFromUserLocation: directionsUseUserLocation,
          directionSourceType,
        }));
      },
    }), [directionsUseUserLocation, dispatch, handleAddressRequest]);

  const handleAddProximityStart = useCallback((proxmityType: ProximityType) =>
    handleAddressRequest({
      callback: (response) => {
        dispatch(setType(proxmityType));
        dispatch(
          setStartingLocation(response, true)
        );
      },
    }), [dispatch, handleAddressRequest]);

  const handleAddMarkerCallback = useCallback(() => {
    if (!latLng) {
      return;
    }

    addLocationFromLatLng({
      latLng,
      onIsLoadingChanged: setIsLoading,
    });
  }, [addLocationFromLatLng, latLng]);

  const handleFindClosesLocation = useCallback(() =>
    handleAddressRequest({
      callback: (response => {
        openFindLocation({ location: response });
      }),
    }), [handleAddressRequest, openFindLocation]);

  const handleDistanceCalculatorDrawingMode = useCallback((mode: DistanceCalculatorDrawingModeEnum) => {
    if (!latLng) {
      return;
    }

    dispatch(startDistanceCalculator());
    dispatch(selectDistanceCalculatorDrawingMode(mode, latLng));
  }, [dispatch, latLng]);

  const handleOpenStreetView = useCallback(() => {
    if (isStreetViewAvailable) {
      dispatch(frontendStateStreetViewSet(streetViewLocation));
    }
  }, [dispatch, isStreetViewAvailable, streetViewLocation]);

  const getPublicMapViewCondition = useCallback((condition: boolean) =>
    !isPresentationalView || condition,
  [isPresentationalView]);

  useEffect(() => {
    if (latLng && previousLatLng !== latLng) {
      handleGetAddress();
    }
  }, [handleGetAddress, latLng, previousLatLng]);

  if (isMapInteractionActive) {
    return null;
  }

  return (
    <MapContextMenuComponent
      addMarker={handleAddMarkerCallback}
      addProximityStart={handleAddProximityStart}
      addWaypoint={handleAddWaypoint}
      address={address}
      findClosestLocation={handleFindClosesLocation}
      getAddress={handleGetAddress}
      googleMap={map}
      isAddDTPAllowed={!isPresentationalView}
      isAddProximityCircleAllowed={getPublicMapViewCondition(publicMapSettings.radiusProximity)}
      isDistanceMeasuringAllowed={getPublicMapViewCondition(publicMapSettings.distanceCalculator)}
      isAddMarkerAllowed={isAddMarkerAllowed}
      isFindClosestLocationAllowed={getPublicMapViewCondition(publicMapSettings.locationFinder)}
      isGetDirectionsAllowed={getPublicMapViewCondition(publicMapSettings.routingDirections)}
      isLoading={isLoading}
      isStreetViewAvailable={isStreetViewAvailable}
      latitude={latLng?.lat ?? 0}
      longitude={latLng?.lng ?? 0}
      openStreetView={handleOpenStreetView}
      setAddress={setAddress}
      setLatLng={setLatLng}
      startDistanceCalculator={handleDistanceCalculatorDrawingMode}
    />
  );
};
